import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import { usePricingService } from "@/lib/hooks/usePricingService";
import { Loading } from "@/components/ui/loading";
import { ErrorState } from "@/components/ui/error-state";
import { preloadWhenIdle } from "@/lib/lazy";
import { BookingPage } from "@/pages/lazyPages";
import { WEEKDAYS, WEEKDAY_LABELS, formatPrice } from "@/lib/booking/pricing";
import { VENUE } from "@/lib/venue";
import { H1, H2, H3, Lead, Muted } from "@/components/ui/typography";
import { ClockIcon, CalendarIcon, ArrowRightIcon, UsersIcon, TargetIcon, MapPinIcon, PhoneIcon, SneakerIcon, TrophyIcon } from "@phosphor-icons/react";

export function HomePage() {
  const navigate = useNavigate();

  // A főoldalról szinte mindig a foglalásra mennek tovább: a kódját tétlen időben előtöltjük
  useEffect(() => preloadWhenIdle([BookingPage]), []);
  // Közös árazási forrás (cache + valós idejű frissítés): visszatéréskor azonnal megvan
  const { pricing, loading: isLoading } = usePricingService();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getPeriods = (dayPricing: any) => {
    if (!dayPricing || !dayPricing.hourlyRates) return [];

    const periods: { name: string, startHour: number, endHour: number, price: number }[] = [];
    const hours = Object.keys(dayPricing.hourlyRates)
      .map(h => parseInt(h))
      .sort((a, b) => {
        const normalizedA = a >= 0 && a < 6 ? a + 24 : a;
        const normalizedB = b >= 0 && b < 6 ? b + 24 : b;
        return normalizedA - normalizedB;
      });

    if (hours.length === 0) return [];

    let currentPeriod = {
      name: "",
      startHour: hours[0],
      endHour: hours[0] + 1,
      price: dayPricing.hourlyRates[hours[0]]
    };

    for (let i = 1; i < hours.length; i++) {
      const hour = hours[i];
      const rate = dayPricing.hourlyRates[hour];
      const normalizedHour = hour >= 0 && hour < 6 ? hour + 24 : hour;
      const normalizedEndHour = currentPeriod.endHour >= 0 && currentPeriod.endHour < 6
        ? currentPeriod.endHour + 24
        : currentPeriod.endHour;

      if (rate === currentPeriod.price && normalizedHour === normalizedEndHour) {
        currentPeriod.endHour = hour + 1;
      } else {
        if (currentPeriod.startHour < 18 && !(currentPeriod.startHour >= 0 && currentPeriod.startHour < 6)) {
          currentPeriod.name = "Napközben";
        } else {
          currentPeriod.name = "Este";
        }
        periods.push({...currentPeriod});

        currentPeriod = {
          name: "",
          startHour: hour,
          endHour: hour + 1,
          price: rate
        };
      }
    }

    if (currentPeriod.startHour < 18 && !(currentPeriod.startHour >= 0 && currentPeriod.startHour < 6)) {
      currentPeriod.name = "Napközben";
    } else {
      currentPeriod.name = "Este";
    }
    periods.push(currentPeriod);

    return periods;
  };

  const formatHour = (hour: number) => {
    if (hour >= 24) {
      const normalizedHour = hour - 24;
      return `${normalizedHour.toString().padStart(2, '0')}:00`;
    }
    return `${hour.toString().padStart(2, '0')}:00`;
  };

  const formatClosingHour = (closingHour: number) => {
    if (closingHour >= 24) {
      const normalizedHour = closingHour - 24;
      return `${normalizedHour.toString().padStart(2, '0')}:00`;
    }
    return `${closingHour.toString().padStart(2, '0')}:00`;
  };

  return (
    <div className="w-full">
      {/* Hero Section */}
      <section className="relative overflow-hidden text-white">
        {/* Background image */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: "url('/arizona-bowling-foglalas-ullo-budapest-hero.webp')" }}
        />
        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 bg-linear-to-b from-black/80 via-black/70 to-black/85" />

        <div className="relative container mx-auto px-4 sm:px-6 py-20 sm:py-28 lg:py-32">
          <div className="max-w-4xl mx-auto text-center">
            {/* Main heading */}
            <H1 className="sm:text-5xl mb-6 animate-fade-in-up">
              Bowlingozz velünk az
              <span className="block text-primary mt-2">Arizona Bowlingban!</span>
            </H1>

            {/* Subtitle */}
            <Lead className="text-white/80 max-w-2xl mx-auto mb-10 animate-fade-in-up" style={{ animationDelay: "0.1s" }}>
              Élvezd a bowling élményét modern környezetben, barátokkal vagy családdal.
              Foglalj időpontot online, egyszerűen és gyorsan!
            </Lead>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up" style={{ animationDelay: "0.2s" }}>
              <Button size="lg" onClick={() => navigate("/booking")}>
                Foglalás most
                <ArrowRightIcon data-icon="inline-end" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => document.getElementById('arak')?.scrollIntoView({ behavior: 'smooth' })}
                className="border-white/40 bg-white/15 hover:bg-white/25 text-white hover:text-white backdrop-blur-xs"
              >
                Árak megtekintése
              </Button>
            </div>

            {/* Quick info */}
            <div className="mt-16 grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-3xl mx-auto animate-fade-in-up" style={{ animationDelay: "0.3s" }}>
              <div className="flex items-center justify-center gap-3 text-white/80">
                <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                  <TargetIcon className="w-5 h-5 text-primary" />
                </div>
                <span className="text-sm">4 profi pálya</span>
              </div>
              <div className="flex items-center justify-center gap-3 text-white/80">
                <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                  <MapPinIcon className="w-5 h-5 text-primary" />
                </div>
                <span className="text-sm">{VENUE.shortAddress}</span>
              </div>
              <div className="flex items-center justify-center gap-3 text-white/80">
                <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                  <PhoneIcon className="w-5 h-5 text-primary" />
                </div>
                <span className="text-sm">{VENUE.phones[0].display}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Wave separator. It hangs a pixel below the section (the section clips it),
            otherwise the dark background can show through as a hairline on the seam:
            the svg is a fraction of a pixel tall, so its bottom edge and the section's
            do not land on the same device pixel. */}
        <div className="absolute -bottom-px left-0 right-0">
          <svg viewBox="0 0 1440 100" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
            <path d="M0 50L48 45.7C96 41.3 192 32.7 288 30.2C384 27.7 480 31.3 576 38.5C672 45.7 768 56.3 864 58.8C960 61.3 1056 55.7 1152 50C1248 44.3 1344 38.7 1392 35.8L1440 33V100H1392C1344 100 1248 100 1152 100C1056 100 960 100 864 100C768 100 672 100 576 100C480 100 384 100 288 100C192 100 96 100 48 100H0V50Z" className="fill-background"/>
          </svg>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 sm:py-24 bg-background">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <H2 className="mb-4">
              Miért válaszd az Arizona Bowlingot?
            </H2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Modern pályák, barátságos légkör és kiváló szolgáltatás vár rád
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {/* Feature Card 1 */}
            <Card className="group">
              <CardHeader>
                <div className="mb-2 flex size-14 items-center justify-center rounded-xl bg-primary/10 transition-colors group-hover:bg-primary/20">
                  <TargetIcon className="size-7 text-primary" />
                </div>
                <CardTitle>Modern pályák</CardTitle>
                <CardDescription>
                  4 teljesen felszerelt, professzionális bowling pálya automatikus pontozórendszerrel és modern felszereléssel.
                </CardDescription>
              </CardHeader>
              <CardFooter>
                <Button
                variant="ghost"
                onClick={() => navigate("/booking")}
              >
                Pálya foglalása
                <ArrowRightIcon data-icon="inline-end" />
              </Button>
              </CardFooter>
            </Card>

            {/* Feature Card 2 */}
            <Card className="group">
              <CardHeader>
                <div className="mb-2 flex size-14 items-center justify-center rounded-xl bg-primary/10 transition-colors group-hover:bg-primary/20">
                  <CalendarIcon className="size-7 text-primary" />
                </div>
                <CardTitle>Egyszerű foglalás</CardTitle>
                <CardDescription>
                  Online időpontfoglalás pár kattintással. Válaszd ki a dátumot, időpontot, és már indulhat is a szórakozás!
                </CardDescription>
              </CardHeader>
              <CardFooter>
                <Button
                variant="ghost"
                onClick={() => navigate("/booking")}
              >
                Foglalás most
                <ArrowRightIcon data-icon="inline-end" />
              </Button>
              </CardFooter>
            </Card>

            {/* Feature Card 3 */}
            <Card className="group">
              <CardHeader>
                <div className="mb-2 flex size-14 items-center justify-center rounded-xl bg-primary/10 transition-colors group-hover:bg-primary/20">
                  <UsersIcon className="size-7 text-primary" />
                </div>
                <CardTitle>Teljes felszerelés</CardTitle>
                <CardDescription>
                  Nem kell saját felszerlést hoznod! Cipőbérlés és különböző súlyú golyók minden vendégnek elérhetőek.
                </CardDescription>
              </CardHeader>
              <CardFooter>
                <Button
                variant="ghost"
                onClick={() => navigate("/booking")}
              >
                Részletek
                <ArrowRightIcon data-icon="inline-end" />
              </Button>
              </CardFooter>
            </Card>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="arak" className="py-16 sm:py-24 bg-linear-to-b from-muted/50 to-background">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
              <TrophyIcon className="w-4 h-4" />
              <span>Kedvező árak</span>
            </div>
            <H2 className="mb-4">
              Nyitvatartás és árak
            </H2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Tekintsd meg aktuális nyitvatartási időnket és árainkat
            </p>
          </div>

          {isLoading ? (
            <Loading label="Árak betöltése..." fullHeight={false} />
          ) : pricing ? (
            <div className="max-w-5xl mx-auto space-y-8">
              {/* Opening Hours */}
              <Card>
                <CardHeader className="border-b">
                  <CardTitle className="flex items-center gap-2">
                    <ClockIcon className="size-5 text-primary" />
                    Nyitvatartási idők
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    {WEEKDAYS.map((day) => {
                      const dayData = pricing[day];
                      const isWeekend = day === 'saturday' || day === 'sunday';
                      return (
                        <div
                          key={day}
                          className={`flex items-center justify-between p-4 rounded-xl border transition-colors ${
                            isWeekend
                              ? 'bg-primary/5 border-primary/20'
                              : 'bg-muted/50 border-border hover:border-border'
                          }`}
                        >
                          <span className={`font-medium ${isWeekend ? 'text-primary' : 'text-foreground'}`}>
                            {WEEKDAY_LABELS[day]}
                          </span>
                          <span className="text-muted-foreground text-sm">
                            {dayData.isOpen
                              ? `${formatHour(dayData.openingHour)}-${formatClosingHour(dayData.closingHour)}`
                              : 'Zárva'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Prices */}
              <Card>
                <CardHeader className="border-b">
                  <CardTitle className="flex items-center gap-2">
                    <TrophyIcon className="size-5 text-primary" />
                    Pályabérlési díjak
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {WEEKDAYS.map((day) => {
                      const dayPricing = pricing[day];
                      if (!dayPricing.isOpen) return null;

                      const periods = getPeriods(dayPricing);
                      if (periods.length === 0) return null;

                      const isWeekend = day === 'saturday' || day === 'sunday';

                      return (
                        <Card key={day} size="sm" className={isWeekend ? "ring-primary/30" : undefined}>
                          <CardHeader>
                            <CardTitle className={isWeekend ? "text-primary" : undefined}>
                              {WEEKDAY_LABELS[day]}
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="gap-2">
                            {periods.map((period, index) => (
                              <div
                                key={index}
                                className="flex items-center justify-between rounded-lg bg-muted/50 p-3"
                              >
                                <div className="flex items-center gap-2">
                                  <ClockIcon className="w-4 h-4 text-muted-foreground/70" />
                                  <span className="text-muted-foreground text-sm">
                                    {`${formatHour(period.startHour)}-${formatHour(period.endHour)}`}
                                  </span>
                                </div>
                                <span className="font-medium text-foreground">
                                  {formatPrice(period.price)}
                                </span>
                              </div>
                            ))}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>

                  {/* Shoe rental */}
                  <div className="mt-6 rounded-xl bg-muted p-5">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <div className="flex size-12 items-center justify-center rounded-xl bg-background text-primary">
                          <SneakerIcon className="size-6" />
                        </div>
                        <div>
                          <p className="font-medium">Cipőbérlés</p>
                          <Muted>Minden méretben elérhető</Muted>
                        </div>
                      </div>
                      <p className="flex items-baseline gap-1">
                        <H3 render={<span />}>{formatPrice(pricing.shoesRentalPrice)}</H3>
                        <Muted render={<span />}>/pár</Muted>
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* CTA */}
              <div className="text-center pt-4">
                <Button size="lg" onClick={() => navigate("/booking")}>
                  Időpont foglalása
                  <ArrowRightIcon data-icon="inline-end" />
                </Button>
              </div>
            </div>
          ) : (
            /*
              Végzett a betöltés, mégsincs adat: tartós hiba (pl. jogosultság). Töltésjelző
              itt azt hazudná, hogy még dolgozik - a látogató a végtelenségig nézné.
            */
            <Card className="max-w-5xl mx-auto">
              <ErrorState
                title="Az árakat most nem sikerült betölteni"
                description="Ellenőrizd az internetkapcsolatot, vagy próbáld újra."
              />
            </Card>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 bg-background">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="relative overflow-hidden rounded-3xl bg-linear-to-br from-primary to-primary/80 p-8 sm:p-12 lg:p-16 text-center">
            {/* Background decoration */}
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute -top-24 -right-24 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
              <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
            </div>

            <div className="relative">
              <H2 className="text-white mb-4">
                Készen állsz a bowlingozásra?
              </H2>
              <Lead className="text-white/80 max-w-2xl mx-auto mb-8">
                Foglald le a pályádat most és élvezd a legjobb bowling élményt Üllőn!
              </Lead>
              <Button size="lg" variant="secondary" onClick={() => navigate("/booking")}>
                Foglalás indítása
                <ArrowRightIcon data-icon="inline-end" />
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
